import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import academicStyles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import styles from "./Viewassessment.module.css";
import { CustomDatePicker } from "../../../../components";

function ListAssessment() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";

  // ==================== STATE ====================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);

  // Individual column search states (matching API payload keys)
  const [titleSearch, setTitleSearch] = useState("");
  const [topicSearch, setTopicSearch] = useState("");
  const [subTopicSearch, setSubTopicSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [questionsCountSearch, setQuestionsCountSearch] = useState("");
  const [createdDateSearch, setCreatedDateSearch] = useState("");
  const [statusSearch, setStatusSearch] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Combined filters object to trigger API re-fetch
  const combinedFilters = useMemo(
    () => ({
      title: titleSearch,
      topic: topicSearch,
      sub_topic: subTopicSearch,
      subject: subjectSearch,
      questions_count: questionsCountSearch,
      created_date: createdDateSearch,
      status: statusSearch,
    }),
    [
      titleSearch,
      topicSearch,
      subTopicSearch,
      subjectSearch,
      questionsCountSearch,
      createdDateSearch,
      statusSearch,
    ],
  );

  // Map of column id -> setter (used to sync cleared filters)
  const filterSetters = useMemo(
    () => ({
      title: setTitleSearch,
      topic: setTopicSearch,
      sub_topic: setSubTopicSearch,
      subject: setSubjectSearch,
      questions_count: setQuestionsCountSearch,
      created_date: setCreatedDateSearch,
      status: setStatusSearch,
    }),
    [],
  );

  // ==================== FETCH ====================
  const fetchListAssessment = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        user_id: user_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        title: titleSearch,
        subject: subjectSearch,
        topic: topicSearch,
        sub_topic: subTopicSearch,
        questions_count: questionsCountSearch,
        created_date: createdDateSearch,
        status: statusSearch,
        search: "",
        academic_id: academic_id,
      };

      const response = await AxiosInstance.post(
        `/book/student/assessmentList`,
        payload,
      );

      if (response?.data?.status) {
        const transformedData = response.data.data.map((item, index) => ({
          id: item.id,
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          title: item.title || "-",
          topic: item.topic_name || item.topic || "-",
          sub_topic: item.sub_topic_name || item.sub_topic || "-",
          subject: item.subject_name || item.subject || "-",
          questions_count: item.num_questions ?? 0,
          created_date: item.created_at
            ? dayjs(item.created_at).format("DD/MM/YYYY")
            : "",
          status: item.status || "Pending",
          client_id: item.client_id,
          school_id: item.school_id,
          class_id: item.class_id,
          section_id: item.section_id,
          isActive: item.isActive,
        }));

        setData(transformedData);
        setRowCount(response.data.pagination?.total || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching assessment list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    user_id,
    pagination.pageIndex,
    pagination.pageSize,
    titleSearch,
    subjectSearch,
    topicSearch,
    subTopicSearch,
    questionsCountSearch,
    createdDateSearch,
    statusSearch,
  ]);

  useEffect(() => {
    if (school_id) {
      fetchListAssessment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, pagination.pageIndex, pagination.pageSize, combinedFilters]);

  // ==================== HANDLERS ====================
  const handleStartAssessment = (row) => {
    navigate("/user/ViewAssessmentTest", {
      state: {
        ID: row.id,
        data: row,
        mode: "start",
      },
    });
  };

  const handleViewResult = (row) => {
    navigate("/user/viewStudentAssessmentResult", {
      state: {
        ID: row.id,
        data: row,
        mode: "result",
      },
    });
  };

  // Sync cleared filters from MRT internal state -> individual search states.
  // When the X (clear) button is clicked, MRT removes the entry from columnFilters
  // (or sets its value to empty). We detect that here and reset the matching
  // search state, which triggers the API refetch with an empty payload.
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;

    Object.keys(filterSetters).forEach((columnId) => {
      const oldFilter = columnFilters.find((f) => f.id === columnId);
      const newFilter = newFilters.find((f) => f.id === columnId);

      const oldValue = oldFilter?.value ?? "";
      const newValue = newFilter?.value ?? "";

      // Filter was cleared (had a value, now empty/removed)
      if (oldValue !== "" && newValue === "") {
        filterSetters[columnId]("");
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    });

    setColumnFilters(newFilters);
  };

  // Fires search on Enter key
  const makeFilterKeyDownHandler = (setter) => (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setter(inputValue);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  // ==================== STATUS BADGE ====================
  const renderStatusBadge = (status) => {
    const value = (status || "").toString().toLowerCase();
    let className = academicStyles.statusBadge;

    if (value === "pending") {
      className = `${academicStyles.statusBadge} ${academicStyles.statusSubmitted}`;
    } else if (value === "pass") {
      className = `${academicStyles.statusBadge} ${academicStyles.statusApproved}`;
    } else if (value === "fail") {
      className = `${academicStyles.statusBadge} ${academicStyles.statusRejected}`;
    }

    return <span className={className}>{status || "-"}</span>;
  };

  // ==================== ACTION BUTTON ====================
  const renderActionButton = (row) => {
    const status = (row.status || "").toString().toLowerCase();

    if (status === "pending") {
      return (
        <button
          type="button"
          onClick={() => handleStartAssessment(row)}
          className={`${academicStyles.addButton} ${styles.btnStyles}`}
        >
          {translations?.labels?.startAssessment || "Start Assessment"}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={() => handleViewResult(row)}
        className={academicStyles.resultButton}
      >
        {translations?.labels?.result || "Result"}
      </button>
    );
  };

  // ==================== SINGLE DATE FILTER ====================
  // Single date picker for the Created Date column.
  // On change, the date is formatted as "YYYY-MM-DD" (e.g. "2026-05-07")
  // and pushed into createdDateSearch -> sent in the API payload.
  const SingleDateFilter = ({ column, translations: t, onDateChange }) => {
    const filterValue = column.getFilterValue();
    const [localDate, setLocalDate] = useState(filterValue || "");

    // Keep local state in sync if the external filter is cleared from outside
    useEffect(() => {
      setLocalDate(filterValue || "");
    }, [filterValue]);

    const dateValue = localDate ? dayjs(localDate) : null;

    const handleChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalDate(formatted);
      // Keep MRT's columnFilters in sync (so chips/badges update)
      column.setFilterValue(formatted || undefined);

      if (typeof onDateChange === "function") {
        onDateChange(formatted);
      }
    };

    return (
      <div className={styles.dateRangeFilter}>
        <CustomDatePicker
          name={`${column.id}_date`}
          value={dateValue}
          onChange={handleChange}
          placeholder={t?.labels?.createdDate || "Created Date"}
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          className={styles.dateRangePicker}
        />
      </div>
    );
  };

  // ==================== COLUMNS ====================
  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: translations?.labels?.title || "Title",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.title || "Title",
          onKeyDown: makeFilterKeyDownHandler(setTitleSearch),
        },
      },
      {
        accessorKey: "topic",
        header: translations?.labels?.topic || "Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.topic || "Topic",
          onKeyDown: makeFilterKeyDownHandler(setTopicSearch),
        },
      },
      {
        accessorKey: "sub_topic",
        header: translations?.labels?.subTopic || "Sub Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.subTopic || "Sub Topic",
          onKeyDown: makeFilterKeyDownHandler(setSubTopicSearch),
        },
      },
      {
        accessorKey: "subject",
        header: translations?.labels?.subject || "Subject",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.subject || "Subject",
          onKeyDown: makeFilterKeyDownHandler(setSubjectSearch),
        },
      },
      {
        accessorKey: "questions_count",
        header: translations?.labels?.questionCount || "Questions Count",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.questionCount || "Questions Count",
          onKeyDown: makeFilterKeyDownHandler(setQuestionsCountSearch),
        },
      },
      {
        accessorKey: "created_date",
        header: translations?.labels?.createdDate || "Created Date",
        enableSorting: true,
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <SingleDateFilter
            column={column}
            translations={translations}
            onDateChange={(formatted) => {
              setCreatedDateSearch(formatted);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        ),
        Cell: ({ cell }) => cell.getValue() || "-",
        size: 200,
      },
      {
        accessorKey: "status",
        header: translations?.labels?.status || "Status",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.status || "Status",
          onKeyDown: makeFilterKeyDownHandler(setStatusSearch),
        },
        Cell: ({ row }) => renderStatusBadge(row.original.status),
      },
      {
        id: "actions",
        header: translations?.labels?.actions || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => renderActionButton(row.original),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations],
  );

  return (
    <div className={academicStyles.container}>
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        enableSearch={false}
      />
    </div>
  );
}

export default ListAssessment;
